import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, List, ListItem, ListItemText, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { selectCanAccess, selectCanDo } from "../app/auth";
import { layersApi } from "../app/layers";
import { orthophotosApi } from "../app/orthophotos";
import SparkMD5 from "spark-md5"; // To check the ETag of uploaded orhtophotos


const ProjectOrthophotosHandler = ({project, setSnackbar}) => {
  const urlParams = useParams();
  const projectId = urlParams.id;
  const canDo = useSelector(selectCanDo);

  const [orthophotos, setOrthophotos] = useState([])

  // upload
  const [selectedFolderWhereToUpload, setSelectedFolderWhereToUpload] = useState('');
  const [orthophotosToUpload, setOrthophotosToUpload] = useState(null);
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [selectedInspectionId, setSelectedInspectionId] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedOrtho, setSelectedOrtho] = useState(null);
  const [selectedInspection, setSelectedInspection] = useState('');

  const ORTHOPHOTOS_STATUS = [
    "Waiting for upload",
    "Uploaded",
    "Uploaded and tiles extracted",
    "Tiles extraction failed",
  ]
  const OUTPUTFIELDS = {
    orthophotos: "id, status, path_prefix",
    createOrthophotos: "orthophoto{id, path_prefix, status}, presignedUrls",
    updateOrthophotostatus: "id, status, projectId",
    createdFromOrthophoto: "id"
  }

  // load data
  useEffect(() => {
    if (!projectId) return;

    // get project orthophotos list
    orthophotosApi.index({ projectId }, OUTPUTFIELDS.orthophotos).then((orthos) => {
      setOrthophotos(orthos);  
    })

    // set project folder as the selected one to upload on
    setSelectedFolderWhereToUpload(project.s3_folders[0]);
  }, [project])


  // Find forbidden characters in path
  const findForbiddenCharacters = (path) => {
    // Allow letters, numbers, /, \, _, -, ., and space
    const forbiddenChars = path.match(/[^a-zA-Z0-9\/_\-\.]/g);
    // Return unique characters or empty array if none
    return forbiddenChars ? [...new Set(forbiddenChars)] : [];
  }

  const handleFolderUpload = async (e) => {
    let new_object = {
      "detected_orthophotos": [],
      "ignored_files": [],
      "uploading": false
    }
  
    const uploadedFiles = Array.from(e.target.files);
    setFilesToUpload(uploadedFiles);
  
    // Detect orthophotos (pair of .tif and .tfw files)
    let per_prefix = {};
    const EPSILON = 1e-10;
  
    const validateTFW = (tfw) => {
      if (!Array.isArray(tfw)) return false;
      if (tfw.length !== 6) return false;
  
      const [a, b, c, d] = tfw;
  
      if (Math.abs(a) <= EPSILON) return false;
      if (Math.abs(d) <= EPSILON) return false;
      if (Math.abs(b) >= EPSILON) return false;
      if (Math.abs(c) >= EPSILON) return false;
  
      const diff = Math.abs(Math.abs(a) - Math.abs(d));
      const sum = Math.abs(a) + Math.abs(d);
      if (sum === 0 || diff / sum >= EPSILON) return false;
  
      return true;
    }
  
    for (const f of uploadedFiles) {
      const file_suffix = f.webkitRelativePath.split('/').slice(1).join('/'); // removing the chosen folder name
      const is_tif = file_suffix.endsWith(".tif");
      const is_tfw = file_suffix.endsWith(".tfw");

      if (is_tif || is_tfw) {
        const path_prefix = project.s3_folders + file_suffix.slice(0, -4);
  
        if (per_prefix[path_prefix] === undefined) {
          per_prefix[path_prefix] = {
            path_prefix,
            has_tif: false,
            has_tfw: false,
            size: "N/A",
            statusText: "TBD",
            isValid: false,
            tif_file: null,
            tfw_file: null
          };
        }

        // Check path
        const forbidden_chars = findForbiddenCharacters(file_suffix.slice(0, -4));
        if (forbidden_chars.length > 0){
          per_prefix[path_prefix].statusText = `Invalid characters in the path: "${forbidden_chars.join("")}"`;
          continue;
        }
  
        if (is_tif) {
          per_prefix[path_prefix].has_tif = true;
          per_prefix[path_prefix].size = Math.round(f.size / 1000000) + "MB";
          per_prefix[path_prefix].tif_file = f;
        }
  
        if (is_tfw) {
          per_prefix[path_prefix].has_tfw = true;
          per_prefix[path_prefix].tfw_file = f;
  
          const text = await f.text();
          const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  
          if (lines.length >= 6) {
            const numbers = lines.slice(0, 6).map(Number);
            const isValid = validateTFW(numbers);
            per_prefix[path_prefix].statusText = isValid ? "Ready for upload" : "TFW invalid ⛔";
            per_prefix[path_prefix].isValid = isValid;
  
            setSnackbar({
              open: true,
              message: isValid
                ? `TFW is valid: ${f.name}`
                : `TFW is invalid: ${f.name}`,
              severity: isValid ? "success" : "error"
            });
          } else {
            per_prefix[path_prefix].statusText = "TFW corrupted ⛔";
            per_prefix[path_prefix].isValid = false;
  
            setSnackbar({
              open: true,
              message: `TFW corrupted: ${f.name} - (there are not 6 lines)`,
              severity: "error"
            });
          }
        }
      } else {
        new_object.ignored_files.push(file_suffix);
      }
    }
  
    new_object.detected_orthophotos = Object.values(per_prefix);
  
    setOrthophotosToUpload(new_object);
  };

  const createOrthophotoFunction = async ({ projectId, path_prefix, partCount }) => {
    return await orthophotosApi.create({ projectId, path_prefix, partCount }, OUTPUTFIELDS.createOrthophotos);
  };

  const handleStartUploading = async (e) => {
    if (!projectId) return
    // Set as "uploading":
    setOrthophotosToUpload((prev) => {
      return {...prev, uploading: true};
    })

    // utility function to update an orthopohoto state
    const updateOrthoState = (path_prefix, new_state) => {
      setOrthophotosToUpload((prev) => {
        for (let ortho of prev.detected_orthophotos){
          if (ortho.path_prefix === path_prefix){
            for (const [key, new_value] of Object.entries(new_state)) {
              ortho[key] = new_value;
            }
            return {...prev, detected_orthophotos: prev.detected_orthophotos};
          }
        }
      })
    }

    // Upload one orthophoto at a time
    for (let ortho of orthophotosToUpload.detected_orthophotos){
      // handle files not being valid
      if (!ortho.isValid) {
        updateOrthoState(ortho.path_prefix, {statusText: "TFW invalid, not uploaded ⛔"});
        setSnackbar({
          open: true,
          message: `Skipping upload: TFW invalid for ${ortho.path_prefix}`,
          severity: "error"
        });
        continue; // passe au suivant
      }

      // orthophoto already has uploaded status
      const alreadyExists = orthophotos.some(
        (o) => o.path_prefix === ortho.path_prefix && o.status >= 1
      );
      if (alreadyExists && !canDo("reupload")) {
        updateOrthoState(ortho.path_prefix, {statusText: "Already uploaded ⛔"});
        setSnackbar({
          open: true,
          message: `Upload blocked: ${ortho.path_prefix} already exists`,
          severity: "warning",
        });
        continue; 
      }

      try {
        // Get tiff file and compute the number of parts (depending on the file size)
        const tiff_file = ortho.tif_file;
        if (tiff_file == null) throw new Error("Could not find the tiff file!")
        const chunkSize = 10000000; // 10 MB
        const partCount = Math.ceil(tiff_file.size / chunkSize);

        // Generate presigned urls:
        const {orthophoto, presignedUrls} = await createOrthophotoFunction({
          projectId: projectId,
          path_prefix: ortho.path_prefix,
          partCount: partCount
        });

        // Upload tfw
        const tfw_file = ortho.tfw_file;
        if (tfw_file == null) throw new Error("Could not find the tfw file!")
        const res_tfw = await fetch(presignedUrls.tfwSignedUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': 'plain/text'
          },
          body: tfw_file
        });
        if (!res_tfw.ok) throw new Error(`TFW upload failed: ${res_tfw.statusText}`);
        updateOrthoState(ortho.path_prefix, {statusText: "Uploading: 1%"});

        // Upload tiff parts
        let parts_uploaded_count = 0; // tracking the parts finished
        // The function to upload a part
        async function uploadPart(partIdx) {
          console.log("PART " + partIdx + "/" + partCount)
          // Make blob
          const start = partIdx * chunkSize;
          const end = Math.min((partIdx+1) * chunkSize, tiff_file.size);
          const blobPart = tiff_file.slice(start, end);
          // Upload part
          let part_response = await fetch(presignedUrls.tiffSignedUrls[partIdx], {
            method: 'PUT',
            headers: {
              'Content-Type': 'image/tiff'
            },
            body: blobPart
          });
          // Check response code and ETag
          if (!part_response.ok) throw new Error(`Failed to upload part ${partIdx}`);
          const etag = part_response.headers.get("Etag"); // The MD5 hash returned by AWS
          const blobPartMd5Hash = SparkMD5.ArrayBuffer.hash(await blobPart.arrayBuffer()); // The MD5 hash computed locally
          if (etag !== `"${blobPartMd5Hash}"`) throw new Error(`Unexpected Etag: ${etag} vs ${blobPartMd5Hash}`);
          // Update progress bar
          parts_uploaded_count++;
          updateOrthoState(ortho.path_prefix, {statusText: `Uploading: ${Math.round(1+(parts_uploaded_count)/partCount*98)}%`});
          // Save ETag (necessary to complete the upload)
          return {
            ETag: etag,
            PartNumber: partIdx+1 // should start at 1
          }
        }

        // Run uploads in batches of MAX_CONCURRENCY
        const partIndexes = Array.from({ length: partCount }, (_, i) => i);
        const MAX_CONCURRENCY = 10;
        const parts_etags = [];
        while (partIndexes.length > 0) {
          const chunk = partIndexes.splice(0, MAX_CONCURRENCY);
          const chunkResults = await Promise.all(chunk.map(uploadPart));
          parts_etags.push(...chunkResults);
        }

        // Make API call to complete the upload and update the orthophoto
        const {id, path_prefix} = orthophoto
        const new_ortho = await orthophotosApi.updateOrthophotoStatus({
          id, 
          status: 1,
          uploadId: presignedUrls.UploadId,
          parts: parts_etags
        }, OUTPUTFIELDS.orthophotos);
        setOrthophotos([
          ...orthophotos.filter((o) => o.path_prefix != new_ortho.path_prefix),
          new_ortho
        ])

        // Update status
        updateOrthoState(ortho.path_prefix, {statusText: "Uploaded! ✅"});
        setSnackbar({
          open: true,
          message: "Orthophotos Uploaded!",
          severity: "info"
        });
      } catch(e) {
        console.log(e)
        updateOrthoState(ortho.path_prefix, {statusText: "Error! ⛔"});
        setSnackbar({
          open: true,
          message: "Orthophotos Upload Error",
          severity: "error"
        });
      }
    }
  }

  useEffect(() => {
    console.log(selectedInspection)
  }, [selectedInspection])


  
  return (
    <Box sx={{ maxWidth: 1200, margin: '2rem auto', padding: '1rem', color: 'white' }}>
      { !project.s3_folders || project.s3_folders.length === 0 ? (
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom color="white">
            Error
          </Typography>
          <Typography variant="body1" color="red">
            No S3 folder found for this project. Upload and layer creation are disabled.
          </Typography>
        </Paper>
      ) : (
        <>
          <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom color="white">
              Orthophotos list
            </Typography>

            {orthophotos && orthophotos.length > 0 ? (
              <TableContainer component={Paper} sx={{ backgroundColor: '#1e1e1e' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Path</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Add Layer</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                  {orthophotos.map((ortho, index) => {
                      return (
                        <TableRow key={index}>
                        <TableCell>
                          {ortho.path_prefix || '—'}
                        </TableCell>
                        <TableCell>
                          {ORTHOPHOTOS_STATUS[ortho.status]}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => { setSelectedOrtho(ortho); setOpenDialog(true); }}
                            disabled={ortho.status !== 2}
                          >
                            Add layer
                          </Button>
                        </TableCell>
                      </TableRow>
                    )}
                  )}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="gray">No orthophotos found.</Typography>
            )}
          </Paper>
          <Paper elevation={3} sx={{ p: 3, mb: 4 }}>

            {/* Upload orthophotos */}
            <Typography variant="h6" gutterBottom color="white">Upload orthophotos</Typography>

            <Paper elevation={1} sx={{ p: 2, mb: 3, backgroundColor: '#2a2a2a', borderRadius: 2 }}>
              <form>
                <FormControl fullWidth margin="normal" size="small">
                  <InputLabel id="select-inspection-label">Select the S3 folder where to upload</InputLabel>
                  <Select
                    labelId="select-inspection-label"
                    value={selectedFolderWhereToUpload}
                    label="Select the S3 folder where to upload"
                    onChange={(e) => setSelectedFolderWhereToUpload(e.target.value)}
                    disabled={orthophotosToUpload}
                  >
                    {project.s3_folders && project.s3_folders.map((s3_folder) => (
                      <MenuItem key={s3_folder} value={s3_folder}>
                        {s3_folder}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {!orthophotosToUpload && (
                  <Button variant="contained" component="label" disabled={orthophotosToUpload}>
                    Select the folder to upload
                    <input
                      type="file"
                      webkitdirectory="true"
                      multiple
                      hidden
                      onChange={handleFolderUpload}
                    />
                  </Button>
                )}
              </form>
            </Paper>

            {orthophotosToUpload && (
              <Paper elevation={1} sx={{ p: 2, mb: 3, backgroundColor: '#2a2a2a', borderRadius: 2 }}>
                <Typography variant="subtitle1" mb={1} color="white">
                  Detected Orthophotos
                </Typography>

                <TableContainer component={Paper} sx={{ mt: 1, mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Path</TableCell>
                        <TableCell>Size</TableCell>
                        <TableCell>Tiff</TableCell>
                        <TableCell>Tfw</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orthophotosToUpload.detected_orthophotos && orthophotosToUpload.detected_orthophotos.map((ortho, index) => (
                        <TableRow key={index}>
                          <TableCell>{ortho.path_prefix}</TableCell>
                          <TableCell>{ortho.size}</TableCell>
                          <TableCell>{ortho.has_tif ? "✅" : "❌"}</TableCell>
                          <TableCell>{ortho.has_tfw && ortho.isValid ? "✅" : "❌"}</TableCell>
                          <TableCell>{ortho.statusText}</TableCell>

                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Button
                  variant="contained"
                  onClick={handleStartUploading}
                  disabled={orthophotosToUpload.uploading}
                >
                  {orthophotosToUpload.uploading ? "Uploading..." : "Start uploading!"}
                </Button>

                <Typography variant="subtitle2" mt={3} mb={1} color="white">
                  {orthophotosToUpload.ignored_files && orthophotosToUpload.ignored_files.length} Ignored files:
                </Typography>
                <List dense>
                  {orthophotosToUpload.ignored_files && orthophotosToUpload.ignored_files.map((file, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={file} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </Paper>
          <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
            <DialogTitle>Choose an inspection</DialogTitle>
            <DialogContent>
              <FormControl fullWidth margin="normal">
                <InputLabel id="select-inspection">Inspection</InputLabel>
                <Select
                sx={{minWidth: "400px" }}
                  labelId="select-inspection"
                  value={selectedInspection}
                  onChange={(e) => {setSelectedInspection(e.target.value);console.log(e.target.value)}}
                >
                  {project.missions?.map((m) =>
                    (m.inspections || []).map((ins) => {
                      return (
                        <MenuItem key={ins.id} value={ins.id}>
                          {m.name} / {ins.name}
                        </MenuItem>
                      )
                    })
                  )}
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDialog(false)}>Annuler</Button>
              <Button
                variant="contained"
                onClick={() => {
                  // to create a Layer we need to have selected an inspection and ortho must be extracted (status: 2)
                  if (!selectedInspection || !selectedOrtho || selectedOrtho.status !== 2) return;

                  layersApi.createFromOrthophoto(
                    { inspectionId: selectedInspection, orthophotoId: selectedOrtho.id, projectId: project.id },
                    OUTPUTFIELDS.createdFromOrthophoto,
                    (layer) => {
                      if (!layer) {
                        setSnackbar({ open: true, message: 'Error while creatig the layer', severity: 'error' });
                      } else {
                        setSnackbar({ open: true, message: 'Layer created sucessfully', severity: 'success' });
                      }
                    }
                  );
                  setOpenDialog(false);
                  setSelectedInspection('');
                  setSelectedOrtho(null);
                }}
                disabled={!selectedInspection}
              >
                Create Layer
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
}

export default ProjectOrthophotosHandler;